<template>
  <div class="conversation-detail-view">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div class="flex items-center gap-4">
        <button class="p-2 text-gray-400 hover:text-gray-600 transition-colors" @click="goBack">
          <svg
            class="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <div>
          <h1 class="text-3xl font-bold text-gray-900">
            {{ conversation?.context.userName || `User ${conversation?.userId}` }}
          </h1>
          <div class="flex items-center gap-4 mt-1 text-sm text-gray-600">
            <span>Chat ID: {{ chatId }}</span>
            <span
              :class="[
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                conversation?.isActive
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800',
              ]"
            >
              {{ conversation?.isActive ? 'Active' : 'Inactive' }}
            </span>
            <span v-if="wsConnected" class="flex items-center gap-1 text-green-600">
              <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              Live
            </span>
          </div>
        </div>
      </div>
      <div class="flex gap-3">
        <button
          :disabled="loading"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          @click="refreshData"
        >
          <svg
            v-if="loading"
            class="animate-spin h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <svg
            v-else
            class="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh
        </button>
        <button
          class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
          @click="exportConversation"
        >
          <svg
            class="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          Export
        </button>
        <button
          class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
          @click="showToolCalls = !showToolCalls"
        >
          <svg
            class="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          {{ showToolCalls ? 'Hide' : 'Show' }} Tools
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading && !conversation" class="flex justify-center items-center h-64">
      <div class="text-center">
        <div
          class="animate-spin mx-auto h-8 w-8 text-blue-600"
          style="
            border: 2px solid;
            border-color: currentColor transparent transparent transparent;
            border-radius: 50%;
          "
        />
        <p class="mt-2 text-gray-600">Loading conversation...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <div class="flex">
        <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd"
          />
        </svg>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Error loading conversation</h3>
          <p class="text-sm text-red-700 mt-1">{{ error }}</p>
        </div>
      </div>
    </div>

    <!-- Conversation Content -->
    <div v-else-if="conversation" class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Messages Panel -->
      <div class="lg:col-span-2">
        <div class="bg-white rounded-lg shadow border">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <svg
                class="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              Messages ({{ messages.length }})
            </h2>
          </div>

          <div class="max-h-96 overflow-y-auto">
            <div v-if="messages.length === 0" class="p-8 text-center">
              <svg
                class="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <p class="mt-2 text-gray-600">No messages in this conversation</p>
            </div>

            <div v-else>
              <div
                v-for="message in messages"
                :key="message.id"
                :class="[
                  'px-6 py-4 border-b border-gray-100 last:border-b-0',
                  message.role === 'user'
                    ? 'bg-blue-50'
                    : message.role === 'assistant'
                      ? 'bg-gray-50'
                      : 'bg-yellow-50',
                ]"
              >
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0">
                    <div
                      :class="[
                        'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                        message.role === 'user'
                          ? 'bg-blue-100 text-blue-600'
                          : message.role === 'assistant'
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-yellow-100 text-yellow-600',
                      ]"
                    >
                      {{
                        message.role === 'user'
                          ? 'U'
                          : message.role === 'assistant'
                            ? 'A'
                            : message.role === 'system'
                              ? 'S'
                              : 'T'
                      }}
                    </div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <span class="text-sm font-medium text-gray-900 capitalize">
                        {{ message.role }}
                      </span>
                      <span
                        v-if="message.metadata?.messageType"
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                      >
                        {{ message.metadata.messageType }}
                      </span>
                      <span class="text-xs text-gray-500">
                        {{ formatTimestamp(message.timestamp) }}
                      </span>
                    </div>
                    <div class="text-sm text-gray-700 whitespace-pre-wrap">
                      {{ message.content }}
                    </div>
                    <div
                      v-if="message.metadata?.toolCalls && message.metadata.toolCalls.length"
                      class="mt-2"
                    >
                      <div class="text-xs text-gray-500 mb-1">Tool calls:</div>
                      <div class="flex flex-wrap gap-1">
                        <span
                          v-for="toolCall in message.metadata.toolCalls"
                          :key="toolCall"
                          class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                        >
                          🔧 {{ toolCall }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="space-y-6">
        <!-- Conversation Info -->
        <div class="bg-white rounded-lg shadow border">
          <div class="px-6 py-4 border-b border-gray-200">
            <h3 class="text-lg font-semibold text-gray-900">Information</h3>
          </div>
          <div class="px-6 py-4 space-y-4">
            <div>
              <dt class="text-sm font-medium text-gray-500">User</dt>
              <dd class="text-sm text-gray-900">
                {{ conversation.context.userName || `User ${conversation.userId}` }}
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500">User ID</dt>
              <dd class="text-sm text-gray-900">{{ conversation.userId }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500">Started</dt>
              <dd class="text-sm text-gray-900">
                {{ formatTimestamp(conversation.conversationStarted) }}
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500">Last Activity</dt>
              <dd class="text-sm text-gray-900">
                {{ formatTimestamp(conversation.lastActivity) }}
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500">Messages</dt>
              <dd class="text-sm text-gray-900">{{ conversation.metadata.messageCount }}</dd>
            </div>
            <div v-if="conversation.context.userLanguage">
              <dt class="text-sm font-medium text-gray-500">Language</dt>
              <dd class="text-sm text-gray-900">{{ conversation.context.userLanguage }}</dd>
            </div>
          </div>
        </div>

        <!-- Topics -->
        <div
          v-if="
            conversation.metadata.conversationTopics &&
              conversation.metadata.conversationTopics.length
          "
          class="bg-white rounded-lg shadow border"
        >
          <div class="px-6 py-4 border-b border-gray-200">
            <h3 class="text-lg font-semibold text-gray-900">Topics</h3>
          </div>
          <div class="px-6 py-4">
            <div class="flex flex-wrap gap-2">
              <span
                v-for="topic in conversation.metadata.conversationTopics"
                :key="topic"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {{ topic }}
              </span>
            </div>
          </div>
        </div>

        <!-- Tools Used -->
        <div
          v-if="conversation.metadata.toolsUsed && conversation.metadata.toolsUsed.length"
          class="bg-white rounded-lg shadow border"
        >
          <div class="px-6 py-4 border-b border-gray-200">
            <h3 class="text-lg font-semibold text-gray-900">Tools Used</h3>
          </div>
          <div class="px-6 py-4">
            <div class="space-y-2">
              <span
                v-for="tool in conversation.metadata.toolsUsed"
                :key="tool"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
              >
                🔧 {{ tool }}
              </span>
            </div>
          </div>
        </div>

        <!-- Preferences -->
        <div v-if="conversation.context.preferences" class="bg-white rounded-lg shadow border">
          <div class="px-6 py-4 border-b border-gray-200">
            <h3 class="text-lg font-semibold text-gray-900">Preferences</h3>
          </div>
          <div class="px-6 py-4 space-y-3">
            <div v-if="conversation.context.preferences.responseStyle">
              <dt class="text-sm font-medium text-gray-500">Response Style</dt>
              <dd class="text-sm text-gray-900 capitalize">
                {{ conversation.context.preferences.responseStyle }}
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500">Tools Enabled</dt>
              <dd class="text-sm text-gray-900">
                {{ conversation.context.preferences.enableTools ? 'Yes' : 'No' }}
              </dd>
            </div>
            <div v-if="conversation.context.preferences.timezone">
              <dt class="text-sm font-medium text-gray-500">Timezone</dt>
              <dd class="text-sm text-gray-900">{{ conversation.context.preferences.timezone }}</dd>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tool Calls Panel -->
    <div v-if="showToolCalls" class="mt-6">
      <div class="bg-white rounded-lg shadow border">
        <div class="px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <svg
              class="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Tool Calls ({{ toolCalls.length }})
          </h2>
        </div>

        <div v-if="loadingTools" class="p-8 text-center">
          <div
            class="animate-spin mx-auto h-6 w-6 text-blue-600"
            style="
              border: 2px solid;
              border-color: currentColor transparent transparent transparent;
              border-radius: 50%;
            "
          />
          <p class="mt-2 text-gray-600">Loading tool calls...</p>
        </div>

        <div v-else-if="toolCalls.length === 0" class="p-8 text-center">
          <svg
            class="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <p class="mt-2 text-gray-600">No tool calls in this conversation</p>
        </div>

        <div v-else class="divide-y divide-gray-200">
          <div v-for="toolCall in toolCalls" :key="toolCall.id" class="px-6 py-4">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-2">
                  <span class="font-medium text-gray-900">{{ toolCall.toolName }}</span>
                  <span
                    :class="[
                      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                      toolCall.status === 'success'
                        ? 'bg-green-100 text-green-800'
                        : toolCall.status === 'error'
                          ? 'bg-red-100 text-red-800'
                          : toolCall.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800',
                    ]"
                  >
                    {{ toolCall.status }}
                  </span>
                  <span class="text-xs text-gray-500">
                    {{ formatTimestamp(toolCall.startTime) }}
                  </span>
                  <span v-if="toolCall.executionTimeMs" class="text-xs text-gray-500">
                    {{ toolCall.executionTimeMs }}ms
                  </span>
                </div>

                <div v-if="Object.keys(toolCall.arguments).length" class="mb-2">
                  <details class="text-sm">
                    <summary class="cursor-pointer text-gray-600 hover:text-gray-900">
                      Arguments
                    </summary>
                    <pre class="mt-1 text-xs bg-gray-50 p-2 rounded border overflow-x-auto">{{
                      JSON.stringify(toolCall.arguments, null, 2)
                    }}</pre>
                  </details>
                </div>

                <div v-if="toolCall.result" class="mb-2">
                  <details class="text-sm">
                    <summary class="cursor-pointer text-gray-600 hover:text-gray-900">
                      Result
                    </summary>
                    <pre class="mt-1 text-xs bg-green-50 p-2 rounded border overflow-x-auto">{{
                      JSON.stringify(toolCall.result, null, 2)
                    }}</pre>
                  </details>
                </div>

                <div v-if="toolCall.error" class="text-sm">
                  <span class="text-red-600 font-medium">Error:</span>
                  <span class="text-red-700">{{ toolCall.error }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { conversationService } from '../services/conversationService';
import type {
  ConversationState,
  ConversationMessage,
  ToolCall,
  WebSocketMessage,
} from '../types/conversation';

const route = useRoute();
const router = useRouter();

// Props
const chatId = route.params.id as string;

// Reactive state
const conversation = ref<ConversationState | null>(null);
const messages = ref<ConversationMessage[]>([]);
const toolCalls = ref<ToolCall[]>([]);
const loading = ref(false);
const loadingTools = ref(false);
const wsConnected = ref(false);
const showToolCalls = ref(false);
const error = ref<string | null>(null);

// Methods
const loadConversation = async () => {
  loading.value = true;
  error.value = null;
  try {
    conversation.value = await conversationService.getConversation(chatId);
    messages.value = conversation.value.messages || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load conversation';
  } finally {
    loading.value = false;
  }
};

const loadToolCalls = async () => {
  loadingTools.value = true;
  try {
    const response = await conversationService.getConversationToolCalls(chatId);
    toolCalls.value = response.toolCalls;
  } catch (err) {
    console.error('Failed to load tool calls:', err);
  } finally {
    loadingTools.value = false;
  }
};

const refreshData = async () => {
  await loadConversation();
  if (showToolCalls.value) {
    await loadToolCalls();
  }
};

const goBack = () => {
  router.push('/conversations');
};

const exportConversation = () => {
  if (conversation.value) {
    conversationService.downloadConversationAsJson(conversation.value);
  }
};

const formatTimestamp = (timestamp: string): string => {
  return conversationService.formatTimestamp(timestamp);
};

// WebSocket handlers
const handleWebSocketMessage = (message: WebSocketMessage) => {
  if (message.type === 'conversation_update' && message.data?.chatId === chatId) {
    // Refresh data on conversation updates
    loadConversation();
  } else if (message.type === 'tool_call_update' && message.data?.chatId === chatId) {
    // Refresh tool calls on tool updates
    if (showToolCalls.value) {
      loadToolCalls();
    }
  }
};

const connectWebSocket = () => {
  conversationService.connectWebSocket(
    handleWebSocketMessage,
    () => {
      wsConnected.value = true;
      conversationService.subscribeToConversation(chatId);
    },
    () => {
      wsConnected.value = false;
    },
    error => {
      console.error('WebSocket error:', error);
      wsConnected.value = false;
    }
  );
};

// Lifecycle
onMounted(async () => {
  await loadConversation();
  connectWebSocket();
});

onUnmounted(() => {
  conversationService.disconnectWebSocket();
});

// Watch for tool calls visibility changes
watch(showToolCalls, show => {
  if (show && toolCalls.value.length === 0) {
    loadToolCalls();
  }
});
</script>

<style scoped>
.conversation-detail-view {
  @apply max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6;
}
</style>
